import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Checkbox, Typography, TextField, List, ListItem, ListItemText, Collapse, IconButton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ShippingApi } from '../api/shippingApi';
import useTranslation from '../../../hooks/useTranslation';

const ShippingZoneModal = ({ open, onClose, existingZone, onSaveZone }) => {
  const { t } = useTranslation();
  const [zoneName, setZoneName] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countries, setCountries] = useState({});
  const [expandedRegions, setExpandedRegions] = useState({});

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await ShippingApi.getCountries();
        const groupedCountries = response.data.reduce((acc, country) => {
          if (!acc[country.region_code]) {
            acc[country.region_code] = { name: country.region_code, countries: [] };
          }
          acc[country.region_code].countries.push(country);
          return acc;
        }, {});
        setCountries(groupedCountries);
      } catch (error) {
        console.error(t('labels.shipping_zone_modal.error_fetching_countries'), error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (existingZone) {
      setZoneName(existingZone.name || '');
      setSelectedCountries(existingZone.regions?.map((region) => region.country_code) || []);
    } else {
      setZoneName('');
      setSelectedCountries([]);
    }
  }, [existingZone]);

  const toggleRegion = (region) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [region]: !prev[region],
    }));
  };

  const handleRegionCheckboxChange = (regionCode, allCountryCodes) => {
    const isSelected = allCountryCodes.every((code) => selectedCountries.includes(code));
    setSelectedCountries((prev) =>
      isSelected ? prev.filter((code) => !allCountryCodes.includes(code)) : [...prev, ...allCountryCodes]
    );
  };

  const handleCountryCheckboxChange = (countryCode) => {
    setSelectedCountries((prev) =>
      prev.includes(countryCode) ? prev.filter((code) => code !== countryCode) : [...prev, countryCode]
    );
  };

  const handleSave = () => {
    if (zoneName && selectedCountries.length > 0) {
      const regions = selectedCountries.map((code) => {
        const country = Object.values(countries)
          .flatMap((region) => region.countries)
          .find((c) => c.country_code === code);
        return {
          country_code: code,
          name: country?.name || '',
          flag: country?.flag || '',
          region_code: country?.region_code || '',
        };
      });      

      const updatedZone = { ...existingZone, name: zoneName, regions };
      onSaveZone(updatedZone);

      setZoneName('');
      setSelectedCountries([]);
      onClose();
    } else {
      alert(t('labels.shipping_zone_modal.validation_error'));
    }
  };

  const filteredCountries = Object.entries(countries).reduce((acc, [regionCode, regionData]) => {
    const filteredRegionCountries = regionData.countries.filter((country) =>
      country.name.toLowerCase().includes(search.toLowerCase())
    );

    if (filteredRegionCountries.length > 0) {
      acc[regionCode] = {
        ...regionData,
        countries: filteredRegionCountries,
      };
    }
    return acc;
  }, {});

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="shipping-zone-modal">
      <div
        style={{
          padding: 20,
          maxWidth: 600,
          margin: '50px auto',
          backgroundColor: 'white',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '70vh',
        }}
      >
        <Typography variant="h6">
          {existingZone ? t('labels.shipping_zone_modal.edit_shipping_zone') : t('labels.shipping_zone_modal.create_shipping_zone')}
        </Typography>

        <TextField
          label={t('labels.shipping_zone_modal.zone_name')}
          variant="outlined"
          fullWidth
          margin="normal"
          value={zoneName}
          onChange={(e) => setZoneName(e.target.value)}
        />

        <TextField
          label={t('labels.shipping_zone_modal.search_countries')}
          variant="outlined"
          fullWidth
          margin="normal"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div style={{ overflowY: 'auto', maxHeight: '50vh', marginBottom: 20 }}>
          <List>
            {Object.entries(filteredCountries).map(([regionCode, regionData]) => {
              const allCountryCodes = regionData.countries.map((c) => c.country_code);
              const isRegionChecked = allCountryCodes.every((code) => selectedCountries.includes(code));
              return (
                <React.Fragment key={regionCode}>
                  <ListItem>
                    <Checkbox
                      checked={isRegionChecked}
                      indeterminate={!isRegionChecked && allCountryCodes.some(code => selectedCountries.includes(code))}
                      onChange={() => handleRegionCheckboxChange(regionCode, allCountryCodes)}
                    />
                    <ListItemText primary={ `${regionData.name} (${allCountryCodes.filter(code => selectedCountries.includes(code)).length} of ${allCountryCodes.length})` } />
                    <IconButton onClick={() => toggleRegion(regionCode)}>
                      {expandedRegions[regionCode] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </ListItem>
                  <Collapse in={expandedRegions[regionCode] || search.length > 0}>
                    <List component="div" disablePadding>
                      {regionData.countries.map((country) => (
                        <ListItem key={country.country_code} sx={{ pl: 4 }}>
                          <Checkbox
                            checked={selectedCountries.includes(country.country_code)}
                            onChange={() => handleCountryCheckboxChange(country.country_code)}
                          />
                          <ListItemText primary={`${country.flag} ${country.name}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onClose} variant="outlined" color="primary">
            {t('labels.shipping_zone_modal.cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {t(existingZone ? 'actions.update' : 'labels.shipping_zone_modal.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ShippingZoneModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingZone: PropTypes.object,
  onSaveZone: PropTypes.func.isRequired,
};

export default ShippingZoneModal;